import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import supabase from '../lib/supabase';

const OrderContext = createContext();

export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};

export const OrderProvider = ({ children }) => {
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orders, setOrders] = useState([]);

  const normalizeOrder = (order) => {
    if (!order || typeof order !== 'object') return order;
    const hasFormattedOrderId = typeof order.orderId === 'string' && /^H2A\d{4}\d{8}$/.test(order.orderId);
    const hasFormattedId = typeof order.id === 'string' && /^H2A\d{4}\d{8}$/.test(order.id);
    // Prefer the formatted orderId when present; ensure both fields match
    if (hasFormattedOrderId && order.id !== order.orderId) {
      return { ...order, id: order.orderId };
    }
    if (!hasFormattedOrderId && hasFormattedId && order.orderId !== order.id) {
      return { ...order, orderId: order.id };
    }
    return order;
  };

  // Load orders from localStorage on initial render
  useEffect(() => {
    try {
      const savedOrders = localStorage.getItem('how2apply_orders');
      if (savedOrders) {
        const parsed = JSON.parse(savedOrders);
        const normalized = Array.isArray(parsed) ? parsed.map(normalizeOrder) : [];
        setOrders(normalized);
      }
    } catch (error) {
      console.error('Error loading orders from localStorage:', error);
    }
  }, []);

  // Save orders to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('how2apply_orders', JSON.stringify(orders));
    } catch (error) {
      console.error('Error saving orders to localStorage:', error);
    }
  }, [orders]);

  // Generate next sequential Order ID with exactly 15 chars: H2A + YYYY + 8-digit counter
  const nextOrderId = () => {
    const now = new Date();
    const year = now.getFullYear();

    // Use timestamp + random component, then take the last 8 digits to
    // keep the format H2A + YYYY + 8 digits while making collisions extremely unlikely
    const timestampPart = Date.now().toString();
    const randomPart = Math.floor(Math.random() * 1000000)
      .toString()
      .padStart(6, '0');

    const combined = `${timestampPart}${randomPart}`;
    const counter8 = combined.slice(-8); // last 8 digits

    const id = `H2A${year}${counter8}`;
    return id.length === 15 ? id : id.slice(0, 15);
  };

  const createOrder = async (orderData) => {
    try {
      console.log("OrderContext: Creating order with data:", orderData);
      const generatedOrderId = nextOrderId();
      const newOrder = normalizeOrder({
        id: generatedOrderId,
        orderId: generatedOrderId,
        ...orderData,
        status: 'pending_payment',
        createdAt: new Date().toISOString(),
      });
      
      console.log("OrderContext: Created new order:", newOrder);
      setCurrentOrder(newOrder);
      
      // Also add to orders list immediately
      setOrders(prev => [...prev, newOrder].map(normalizeOrder));

      // Sync to Supabase for operator/admin visibility
      try {
        const authUserRaw = localStorage.getItem('how2apply_user');
        const authUser = authUserRaw ? JSON.parse(authUserRaw) : null;
        // Also try extracting user from the token payload as a fallback
        let tokenUser = null;
        try {
          const token = localStorage.getItem('how2apply_token');
          if (token) {
            const payload = JSON.parse(atob(token));
            tokenUser = payload?.user || null;
          }
        } catch (tokenErr) {
          console.warn('OrderContext: Failed to decode session token for fallback user info', tokenErr);
        }

        // Normalize fields for available_orders_a7b5c9
        let serviceTypeLabel = newOrder.serviceName || newOrder.serviceType || 'Service';
        if (typeof serviceTypeLabel === 'string' && serviceTypeLabel.length > 120) {
          serviceTypeLabel = serviceTypeLabel.slice(0, 120);
        }
        const applicationFee = newOrder.applicationFee ?? newOrder.examFee ?? 0;
        const serviceFee = newOrder.serviceFee ?? 0;
        const docMap = Array.isArray(newOrder.documents)
          ? Object.fromEntries(newOrder.documents.map((d) => [String(d), true]))
          : (newOrder.documents && typeof newOrder.documents === 'object' ? newOrder.documents : {});
        // Limit number of document keys to avoid oversized rows
        const documents = Object.fromEntries(Object.entries(docMap).slice(0, 20));

        const safeCustomerId = authUser?.id || tokenUser?.id || null;
        const safeCustomerName = authUser?.name || tokenUser?.name || 'Customer';
        const safeCustomerEmail = authUser?.email || tokenUser?.email || null;
        const safeCustomerPhone = authUser?.mobile || tokenUser?.mobile || null;

        const { error } = await supabase
          .from('available_orders_a7b5c9')
          .upsert({
            order_id: newOrder.orderId,
            customer_id: safeCustomerId,
            customer_name: safeCustomerName,
            customer_email: safeCustomerEmail,
            customer_phone: safeCustomerPhone,
            service_type: serviceTypeLabel,
            application_fee: applicationFee,
            service_fee: serviceFee,
            documents,
            status: 'available',
            created_at: new Date().toISOString()
          }, { onConflict: 'order_id' });

        if (error) {
          console.error('Supabase insert error (available_orders_a7b5c9):', error);
          // Retry with minimal required fields to avoid transient schema issues
          try {
            const { error: minimalErr } = await supabase
              .from('available_orders_a7b5c9')
              .insert({
                order_id: newOrder.orderId,
                service_type: serviceTypeLabel,
                customer_name: safeCustomerName || 'Customer',
                status: 'available'
              });
            if (minimalErr) {
              console.error('Minimal insert also failed:', minimalErr);
              toast.error(`Unable to publish order to operators: ${minimalErr.message || 'Unknown error'}`);
            } else {
              console.warn('Published with minimal fields due to previous error');
              toast.success('Order published with limited details; you may proceed.');
            }
          } catch (reErr) {
            console.error('Retry insert exception:', reErr);
            toast.error('Unable to publish order to operators. Please try again.');
          }
        }

        // No separate 'orders' table sync; operator and customer views use available_orders_a7b5c9
      } catch (syncError) {
        console.error('Error syncing order to Supabase:', syncError);
      }
      
      return newOrder;
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order");
      return null;
    }
  };

  const updateOrder = (orderId, updates) => {
    try {
      setOrders(prev => prev.map(order => 
        order.id === orderId ? normalizeOrder({ ...order, ...updates }) : order
      ));
      
      if (currentOrder?.id === orderId) {
        setCurrentOrder(prev => normalizeOrder({ ...prev, ...updates }));
      }
      
      console.log("Updated order:", orderId, updates);
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order");
    }
  };

  const addOrder = (order) => {
    try {
      // Check if order already exists in the list
      const exists = orders.some(o => o.id === order.id);
      
      if (!exists) {
        setOrders(prev => [...prev, normalizeOrder(order)]);
        console.log("Added order to list:", order);
      } else {
        // Update existing order
        setOrders(prev => prev.map(o => o.id === order.id ? normalizeOrder(order) : o));
        console.log("Updated existing order in list:", order);
      }
    } catch (error) {
      console.error("Error adding order:", error);
      toast.error("Failed to save order");
    }
  };

  const getOrderById = (orderId) => {
    try {
      console.log("OrderContext: getOrderById called with orderId:", orderId);
      console.log("OrderContext: Current orders list:", orders);
      console.log("OrderContext: Current order:", currentOrder);
      
      // First check in orders list by either internal id or external orderId
      let order = orders.find(order => order.id === orderId || order.orderId === orderId);
      
      // If not found in orders list, check currentOrder
      if (!order && currentOrder && (currentOrder.id === orderId || currentOrder.orderId === orderId)) {
        console.log("OrderContext: Order found in currentOrder");
        order = currentOrder;
      }
      
      console.log("OrderContext: Final result - Found order:", order ? "YES" : "NO", order);
      
      return order;
    } catch (error) {
      console.error("Error getting order by ID:", error);
      return null;
    }
  };

  const value = {
    currentOrder,
    orders,
    createOrder,
    updateOrder,
    addOrder,
    getOrderById,
    setCurrentOrder,
    setOrders,
  };

  return (
    <OrderContext.Provider value={value}>
      {children}
    </OrderContext.Provider>
  );
};